/**
 * Runtime validation utilities for WebSocket messages
 */

import {
  WebSocketMessage,
  MessageType,
  JobCreatedMessage,
  JobProgressMessage,
  JobCompletedMessage,
  JobFailedMessage,
  JobCancelledMessage,
  ErrorMessage,
  JobStats,
} from './bulkIndexing';

/**
 * Type guard to check if a value is a valid MessageType
 */
function isValidMessageType(value: unknown): value is MessageType {
  return (
    typeof value === 'string' &&
    [
      'job_created',
      'job_progress',
      'job_completed',
      'job_failed',
      'job_cancelled',
      'error',
    ].includes(value)
  );
}

/**
 * Type guard to check if a value is a valid JobStats object
 */
function isValidJobStats(value: unknown): value is JobStats {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  // JobStats is flexible with index signature, so just check it's an object
  return true;
}

/**
 * Type guard to check if a value is a valid BaseWebSocketMessage
 */
function isValidBaseMessage(value: unknown): value is {
  message_type: MessageType;
  timestamp: string;
  job_id: string;
} {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return (
    isValidMessageType(obj.message_type) &&
    typeof obj.timestamp === 'string' &&
    typeof obj.job_id === 'string'
  );
}

/**
 * Validate and parse WebSocket message with runtime type checking
 */
export function validateWebSocketMessage(data: unknown): WebSocketMessage {
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (error) {
      throw new Error(`Invalid JSON in WebSocket message: ${error}`);
    }
  }

  if (!isValidBaseMessage(data)) {
    throw new Error('Invalid WebSocket message: missing required fields (message_type, timestamp, job_id)');
  }

  const base = {
    message_type: data.message_type,
    timestamp: data.timestamp,
    job_id: data.job_id,
  };

  switch (base.message_type) {
    case 'job_created': {
      const msg = data as Record<string, unknown>;
      if (typeof msg.target_type !== 'string') {
        throw new Error('Invalid job_created message: target_type must be a string');
      }
      return {
        ...base,
        message_type: 'job_created',
        target_type: msg.target_type as 'full' | 'account' | 'workspace' | 'project',
        target_id: typeof msg.target_id === 'string' ? msg.target_id : undefined,
      } as JobCreatedMessage;
    }

    case 'job_progress': {
      const msg = data as Record<string, unknown>;
      if (!isValidJobStats(msg.progress)) {
        throw new Error('Invalid job_progress message: progress must be an object');
      }
      return {
        ...base,
        message_type: 'job_progress',
        progress: msg.progress as JobStats,
      } as JobProgressMessage;
    }

    case 'job_completed': {
      const msg = data as Record<string, unknown>;
      if (!isValidJobStats(msg.stats)) {
        throw new Error('Invalid job_completed message: stats must be an object');
      }
      return {
        ...base,
        message_type: 'job_completed',
        stats: msg.stats as JobStats,
      } as JobCompletedMessage;
    }

    case 'job_failed': {
      const msg = data as Record<string, unknown>;
      if (typeof msg.error !== 'string') {
        throw new Error('Invalid job_failed message: error must be a string');
      }
      return {
        ...base,
        message_type: 'job_failed',
        error: msg.error,
      } as JobFailedMessage;
    }

    case 'job_cancelled': {
      return {
        ...base,
        message_type: 'job_cancelled',
      } as JobCancelledMessage;
    }

    case 'error': {
      const msg = data as Record<string, unknown>;
      if (typeof msg.error !== 'string') {
        throw new Error('Invalid error message: error must be a string');
      }
      return {
        ...base,
        message_type: 'error',
        error: msg.error,
      } as ErrorMessage;
    }

    default: {
      // TypeScript exhaustiveness check
      const _exhaustive: never = base.message_type;
      throw new Error(`Unknown message type: ${_exhaustive}`);
    }
  }
}

/**
 * Type guard to check if a value is a valid WebSocketMessage
 */
export function isValidWebSocketMessage(value: unknown): value is WebSocketMessage {
  try {
    validateWebSocketMessage(value);
    return true;
  } catch {
    return false;
  }
}

